import React, { useEffect, useState, useRef } from 'react';
import {
  Accordion,
  AccordionItem,
  Checkbox,
  Select,
  SelectItem,
} from '@heroui/react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

export function Medicine({ answerHistory, data, medicineHistory, sendAnswer }) {
  const { t } = useTranslation('survey');

  let initAnswer = Array.from({ length: data.length }).map(() => ({}));
  const initSelectedMeds = new Set();

  if (answerHistory) {
    initAnswer = answerHistory;
  } else if (medicineHistory) {
    const medProfile = JSON.parse(medicineHistory);

    data.forEach((item, i) => {
      const key = item.options.medicineId;
      if (medProfile[key]) {
        initAnswer[i] = medProfile[key];
      }
    });
  }

  initAnswer.forEach((answer, i) => {
    if (!_.isEmpty(answer)) {
      initSelectedMeds.add(data[i].text);
    }
  });

  const [answers, setAnswer] = useState(initAnswer);
  const [errors, setErrors] = useState(new Array(data.length).fill(false));
  const [selectedMeds, setSelectedMeds] = React.useState(initSelectedMeds);
  const scrollLockRef = useRef(null);

  useEffect(() => {
    if (!errors.includes(true)) {
      sendAnswer(answers);
    } else {
      sendAnswer(null);
    }
  }, [answers, errors]);

  const handleUncheckMeds = (selected) => {
    const answersCopy = [...answers];
    data.forEach((med, i) => {
      if (!_.isEmpty(answers[i]) && !selected.has(med.text)) {
        answersCopy[i] = {};
      }
    });
    setAnswer(answersCopy);
  };

  const validateSelectedMeds = (selected) => {
    const errorsCopy = new Array(data.length).fill(false);

    selected.forEach((selected) => {
      const medIndex = _.findIndex(data, ['text', selected]);

      const answer = answers[medIndex];

      if (_.isEmpty(answer) || Object.keys(answer).length < 2) {
        errorsCopy[medIndex] = true;
      } else {
        errorsCopy[medIndex] = false;
      }
    });

    setSelectedMeds(selected);
    setErrors(errorsCopy);
    handleUncheckMeds(selected);
  };

  const handleSelect = (key, value, i) => {
    const answersCopy = [...answers];
    const errorsCopy = [...errors];

    const medicine = answersCopy[i];
    medicine[key] = value;

    if (Object.keys(medicine).length < 2) {
      errorsCopy[i] = true;
    } else {
      errorsCopy[i] = false;
    }

    setErrors(errorsCopy);
    setAnswer(answersCopy);
  };

  const handleDropdownOpenChange = (isOpen) => {
    // When choosing pills and sometimes the dose for medicines at the bottom of the page,
    // the page scrolls up a bit automatically, which is annoying.
    // This only happens in the mobile web view when the screen is narrow enough.
    // To fix this, we need to lock the scroll position when the dropdown opens

    if (isOpen) {
      // Lock scroll position when dropdown opens
      const scrollY = window.scrollY;
      scrollLockRef.current = scrollY;

      const lockScroll = () => {
        if (scrollLockRef.current !== null) {
          window.scrollTo(0, scrollLockRef.current);
        }
      };

      // Lock scroll immediately and after a brief delay
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollY);
      });

      window.addEventListener('scroll', lockScroll, {
        passive: false,
      });

      // Release lock after dropdown has time to render
      setTimeout(() => {
        window.removeEventListener('scroll', lockScroll);
        scrollLockRef.current = null;
      }, 500);
    } else {
      // Release lock when dropdown closes
      scrollLockRef.current = null;
    }
  };

  return (
    <Accordion
      onSelectionChange={validateSelectedMeds}
      defaultExpandedKeys={selectedMeds}
      selectionMode="multiple"
    >
      {data.map((item, i) => (
        <AccordionItem
          key={item.text}
          aria-label={item.text}
          title={item.text}
          startContent={
            <Checkbox
              aria-label={item.text}
              isSelected={selectedMeds.has(item.text)}
              isDisabled
            />
          }
        >
          <div className="flex flex-col gap-4 my-4">
            <Select
              className="max-w-xs"
              data-testid={`${item.text}-dose`}
              defaultSelectedKeys={[answers[i].dose]}
              isInvalid={!answers[i].dose}
              isRequired
              label={t('medicine.input.dose')}
              onChange={(e) => handleSelect('dose', e.target.value, i)}
              onOpenChange={handleDropdownOpenChange}
              popoverProps={{
                shouldCloseOnScroll: false, // prevent the dropdown from closing when the page automatically scrolls up
                placement: 'bottom',
              }}
            >
              {item.options.doses.map((dose) => (
                <SelectItem key={dose}>{dose}</SelectItem>
              ))}
            </Select>
            <Select
              className="max-w-xs"
              data-testid={`${item.text}-pills`}
              defaultSelectedKeys={[answers[i].pills]}
              isInvalid={!answers[i].pills}
              isRequired
              label={t('medicine.input.count')}
              onChange={(e) => handleSelect('pills', e.target.value, i)}
              onOpenChange={handleDropdownOpenChange}
              popoverProps={{
                shouldCloseOnScroll: false, // prevent the dropdown from closing when the page automatically scrolls up
                placement: 'bottom',
              }}
            >
              {_.range(1, 21).map((count) => (
                <SelectItem key={count.toString()}>
                  {count.toString()}
                </SelectItem>
              ))}
            </Select>
          </div>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
